import { registerActions } from "../actionRegistry";
import { api } from "../api";
import { toastSuccess, toastError } from "../ui/messages";
import { closeModalLast, refreshModal, closeModalLastRefresh } from "../ui/modal";
import { getDomElement, getDomElementOrThrow, setHtml, queryAll } from "../utils/dom";

/**
 * The `.variant-all` / `.variant-<id>` elements ARE the checkboxes, so the legacy
 * `getDomElement($(this).attr("id"))` round-trip resolved back to the same element.
 */
function variantCheckboxes(selector: string): HTMLInputElement[] {
  return Array.from(queryAll<HTMLInputElement>(selector)).filter((el) => el.id !== "");
}

/** How many `.variant-all` checkboxes are currently ticked. */
function countCheckedVariants(): number {
  return variantCheckboxes(".variant-all").filter((el) => el.checked).length;
}

export function variantselect(id: string | number): void {
  let selectcount = 0;

  for (const el of variantCheckboxes(id === "all" ? ".variant-all" : `.variant-${id}`)) {
    if (!el.checked) {
      el.checked = true;
      selectcount++;
    }
  }

  toastSuccess(`+${selectcount} selected, total selected is ${countCheckedVariants()}`);
}

export function variantmulti(what: "enable" | "disable"): void {
  for (const el of variantCheckboxes(".variant-all")) {
    if (!el.checked) continue;
    const raw_id = el.id.replace("select-", "");
    const disabledEl = getDomElement<HTMLInputElement>(`disabled_${raw_id}`);
    if (what === "disable" && disabledEl?.value === "0") variantdisable(raw_id);
    if (what === "enable" && disabledEl?.value === "1") variantdisable(raw_id);
  }
}

export function variantdisable(id: string): void {
  const disabledEl = getDomElement<HTMLInputElement>("disabled_" + id);
  if (!disabledEl) return;

  const nowDisabled = disabledEl.value !== "1";
  getDomElement("variant-row-" + id)?.classList.toggle(
    "ecommerce-variantconfig-data-disabled",
    nowDisabled
  );
  disabledEl.value = nowDisabled ? "1" : "0";
  // data-action/data-fn instead of re-binding a click after every innerHTML swap.
  setHtml(
    "disablecontainer_" + id,
    `<span class="button small ${nowDisabled ? "green" : "red"}" data-action="call" data-fn="variantdisable" data-args="${id}">${nowDisabled ? "Enable" : "Disable"}</span>`
  );
}

/** The "set values for all selected variants" form, owned by WithSelectedModal. */
export interface VariantMultiValues {
  price: string;
  weight: string;
  sku: string;
  passepartout: string;
  overwrite: boolean;
}

/**
 * Apply one set of values to every checked variant row.
 *
 * The values are passed in: WithSelectedModal renders those inputs, so it owns
 * them. What stays id-driven here is the *other* half — writing into the variant
 * rows on the page behind the modal, which genuinely are not this component's.
 */
export function variantmultido(values: VariantMultiValues): void {
  let selectcount = 0;

  function setField(multiVal: string, targetId: string, overwrite: boolean): void {
    if (!multiVal) return;
    const el = getDomElement<HTMLInputElement>(targetId);
    if (!el) return;
    if (overwrite || el.value === "") {
      el.value = multiVal;
      selectcount++;
    }
  }

  function setImage(aspect: string, raw_id: string): void {
    const multiVal = getDomElement<HTMLInputElement>(`multi_image_${aspect}`)?.value;
    if (!multiVal) return;
    getDomElementOrThrow<HTMLInputElement>(`image_${raw_id}`).value = multiVal;
    const imgSrc = getDomElementOrThrow<HTMLImageElement>(`multi_image_src_${aspect}`).src;
    setHtml(
      `set-defaultimg-${raw_id}`,
      `<img src="${imgSrc}" class="ecommerce-variantconfig-image">`
    );
  }

  for (const el of variantCheckboxes(".variant-all")) {
    if (!el.checked) continue;
    const raw_id = el.id.replace("select-", "");

    const overwrite = values.overwrite;

    setField(values.price, `price_${raw_id}`, overwrite);
    setField(values.weight, `weight_${raw_id}`, overwrite);
    setField(values.sku, `sku_${raw_id}`, overwrite);
    setField(values.passepartout, `passepartout_${raw_id}`, overwrite);

    if (!getDomElement(`set-defaultimg-${raw_id}`)) continue;

    const imageAspect = getDomElementOrThrow<HTMLInputElement>(`image_aspect_${raw_id}`).value;
    if (imageAspect === "p" || imageAspect === "s" || imageAspect === "l") {
      setImage(imageAspect, raw_id);
    }
  }

  closeModalLast();
  toastSuccess(`${selectcount} changed`);
}

export function variantdeselect(id: string | number): void {
  let selectcount = 0;

  for (const el of variantCheckboxes(id === "all" ? ".variant-all" : `.variant-${id}`)) {
    if (el.checked) {
      el.checked = false;
      selectcount++;
    }
  }

  toastSuccess(`-${selectcount} de-selected, total selected is ${countCheckedVariants()}`);
}

export async function variantoptionssort(variant_id: string): Promise<void> {
  const order = Array.from(queryAll<HTMLElement>(".ecommerce-variantoption"), (el) =>
    el.id.replace("variantoption-", "")
  ).filter((raw_id) => raw_id !== "");

  if (!order.length) return;
  try {
    await api.ecommerce.saveVariantOptionsOrder({ id: variant_id, order });
    toastSuccess("Order updated");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function deletevariantoption(variantoption_id: string): Promise<void> {
  try {
    await api.ecommerce.deleteVariantOption({ id: variantoption_id });
    toastSuccess("Variant option deleted");
    refreshModal();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

/**
 * `displayAs` is passed in rather than read back out of the modal's own input:
 * EditVariantModal renders that field, so it owns the value.
 */
export async function addvariantoption(
  variant_id: string,
  name: string,
  displayAs?: string
): Promise<void> {
  const display_as = displayAs || name;
  try {
    await api.ecommerce.addVariantOption({
      variantId: variant_id,
      name,
      displayAs: display_as,
    });
    toastSuccess("Variant option added");
    // The modal re-renders and its option input carries autoFocus, so the old
    // "wait 100ms then find the input by id" step is no longer needed.
    refreshModal();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function deletevariant(variant_id: string): Promise<void> {
  if (!confirm("Are you sure you want to remove the variant and all options?")) return;
  try {
    await api.ecommerce.deleteVariant({ id: variant_id });
    toastSuccess("Variant deleted");
    refreshModal();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function addvariant(product_id: string): Promise<void> {
  const variantNameEl = getDomElement<HTMLInputElement>("variant_name");
  const variantTypeEl = getDomElement<HTMLInputElement>("add_variant_type");
  if (!variantNameEl?.value || !variantTypeEl?.value) return;
  try {
    await api.ecommerce.addVariant({
      productId: product_id,
      type: variantTypeEl.value as "custom" | "size",
      name: variantNameEl.value,
    });
    toastSuccess("Variant added");
    closeModalLastRefresh();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

registerActions({
  variantdisable,
});
